import * as React from 'react';
import {
  ISelectionZoneProps,
  IObjectWithKey
} from 'office-ui-fabric-react/lib/Selection';
import { Fragment } from '../../utilities/components/Fragment';
import { asAsync } from '../../utilities/components/Async';
import { IComponentDecorator, IDecoratedProps } from '../../utilities/components/Decorators';
import { afterPageLoad } from './afterPageLoad';
import { ShowOnlyWithSelection } from '../../components/selectionVisibility/SelectionVisibility';
import { EventGroup } from '@uifabric/utilities';

const AsyncItemsContextMenu = afterPageLoad()(asAsync(async () => (
  await import(/* webpackChunkName: 'deferredcomponents' */'../DeferredComponents')
).ItemsContextMenu));

/**
 * @public
 */
export type IWithSelectedItemsContextMenuProps = Pick<ISelectionZoneProps, 'onItemContextMenu'>;

interface IActionManagerState {
  isContextMenuOpen?: boolean;
  contextMenuTarget?: MouseEvent | HTMLElement;
}

/**
 * @public
 */
export type IDecorateWithSelectedItemsContextMenuProps<TProps extends IWithSelectedItemsContextMenuProps> =
  IDecoratedProps<TProps, IWithSelectedItemsContextMenuProps, {}>;

/**
 * @internal
 */
export function withSelectedItemsContextMenu<TInjectedProps, TOwnProps = {}>(
  mapToProps: (contextMenuProps: IWithSelectedItemsContextMenuProps, props: TOwnProps) => TInjectedProps
): IComponentDecorator<TInjectedProps, TOwnProps>;
export function withSelectedItemsContextMenu():
  IComponentDecorator<IWithSelectedItemsContextMenuProps, {}>;
export function withSelectedItemsContextMenu<TInjectedProps, TOwnProps = {}>(
  mapToProps?: (contextMenuProps: IWithSelectedItemsContextMenuProps, props: TOwnProps) => TInjectedProps
): IComponentDecorator<TInjectedProps & IWithSelectedItemsContextMenuProps, TOwnProps> {
  return <P extends TInjectedProps & IWithSelectedItemsContextMenuProps>(
    component: React.ComponentType<P>
  ): React.ComponentType<IDecoratedProps<P, TInjectedProps & IWithSelectedItemsContextMenuProps, TOwnProps>> => {
    type IProps = IDecoratedProps<P, TInjectedProps & IWithSelectedItemsContextMenuProps, TOwnProps>;

    const Component = component;
    const CONTEXTMENU_DISMISS = 'DocLibContextMenuDismissed';

    class WithSelectedItemsContextMenu extends React.PureComponent<IProps, IActionManagerState> {
      private _contextMenuProps: IWithSelectedItemsContextMenuProps;

      constructor(props: IProps) {
        super(props);

        this.state = {};

        this._contextMenuProps = {
          onItemContextMenu: this._onItemContextMenu
        };
      }

      public render(): JSX.Element {
        const {
          props,
          state
        } = this;

        const contextMenuProps = this._contextMenuProps;

        return (
          <Fragment>
            <Component
              { ...props }
              { ...(mapToProps ? mapToProps(contextMenuProps, props as {} as TOwnProps) : contextMenuProps) }
            />
            <ShowOnlyWithSelection>
              <AsyncItemsContextMenu
                target={ state.contextMenuTarget }
                onDismiss={ this._onDismissContextMenu }
                isOpen={ !!state.isContextMenuOpen }
              />
            </ShowOnlyWithSelection>
          </Fragment>
        );
      }

      private _onItemContextMenu = (item: IObjectWithKey | undefined, index: number | undefined, ev: Event | undefined): void => {
        if (ev) {
          this.setState({
            isContextMenuOpen: true,
            contextMenuTarget: ev instanceof MouseEvent ? ev : ev.target as HTMLElement
          });

          ev.stopPropagation();
        }
      }

      private _onDismissContextMenu = (): void => {
        this.setState({
          isContextMenuOpen: false,
          contextMenuTarget: undefined
        });
        // TODO Avoid firing a global event. Instead, use the event which triggered the context menu to send
        // a signal back to the invoker.
        EventGroup.raise(document, CONTEXTMENU_DISMISS);
      }
    }

    return WithSelectedItemsContextMenu as {} as React.ComponentType<IDecoratedProps<P,
      TInjectedProps & IWithSelectedItemsContextMenuProps,
      TOwnProps>>;
  };
}
